import {
  BarChart3,
  Bot,
  Boxes,
  Braces,
  ServerCog,
  PlugZap,
  ChevronDown,
  Circle,
  CloudUpload,
  Code2,
  Command,
  Database,
  GitBranch,
  Home,
  HelpCircle,
  BookOpenCheck,
  MessageSquareText,
  Library,
  LayoutDashboard,
  Monitor,
  Moon,
  Play,
  Search,
  Settings,
  Sparkles,
  Workflow,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStudio } from "../studioStore";
import type { StudioView } from "../types";
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES, type SupportedLanguage } from "../i18n/countryLanguageMap";
import { setLanguage } from "../i18n";
import { BlackedOnboarding } from "./BlackedOnboarding";

interface NavItem {
  id: StudioView;
  labelKey: string;
  icon: typeof Home;
  shortcut?: string;
}

const navItems: NavItem[] = [
  { id: "home", labelKey: "nav.home", icon: LayoutDashboard, shortcut: "1" },
  { id: "designer", labelKey: "nav.designer", icon: Boxes, shortcut: "2" },
  { id: "code", labelKey: "nav.code", icon: Code2, shortcut: "3" },
  { id: "data", labelKey: "nav.data", icon: Database, shortcut: "4" },
  { id: "workflows", labelKey: "nav.workflows", icon: Workflow, shortcut: "5" },
  { id: "dashboard", labelKey: "nav.dashboard", icon: BarChart3 },
  { id: "backend", labelKey: "nav.backend", icon: ServerCog },
  { id: "integrations", labelKey: "nav.integrations", icon: PlugZap },
  { id: "git", labelKey: "nav.git", icon: GitBranch, shortcut: "6" },
  { id: "deploy", labelKey: "nav.deploy", icon: CloudUpload, shortcut: "7" },
  { id: "assistant", labelKey: "nav.assistant", icon: Bot, shortcut: "8" },
];

export function StudioShell({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation();
  const { project, view, setView } = useStudio();
  const [commandOpen, setCommandOpen] = useState(false);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen((open) => !open);
      }
      if ((event.ctrlKey || event.metaKey) && /^[1-8]$/.test(event.key)) {
        const item = navItems.find((candidate) => candidate.shortcut === event.key);
        if (item) {
          event.preventDefault();
          setView(item.id);
        }
      }
      if (event.key === "Escape") {
        setCommandOpen(false);
        setProjectMenuOpen(false);
        setHelpMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [setView]);

  const localizedNavItems = useMemo(
    () => navItems.map((item) => ({ ...item, label: t(item.labelKey) })),
    [t],
  );

  const activeItem = useMemo(
    () => {
      if (view.startsWith("help-")) return { id: view, label: t("nav.help"), icon: HelpCircle };
      return localizedNavItems.find((item) => item.id === view) || {
        id: "settings" as const,
        label: t("nav.settings"),
        icon: Settings,
      };
    },
    [view, localizedNavItems, t],
  );
  const ActiveIcon = activeItem.icon;

  const quickCommands = [
    {
      label: t("command.openDesigner"),
      hint: t("command.openDesignerHint"),
      icon: Monitor,
      view: "designer" as StudioView,
    },
    {
      label: t("command.createDataContract"),
      hint: t("command.createDataContractHint"),
      icon: Database,
      view: "data" as StudioView,
    },
    {
      label: t("command.designEndpoint"),
      hint: t("command.designEndpointHint"),
      icon: Braces,
      view: "workflows" as StudioView,
    },
    {
      label: t("command.askAssistant"),
      hint: t("command.askAssistantHint"),
      icon: Sparkles,
      view: "assistant" as StudioView,
    },
  ];

  return (
    <div className="studio-shell">
      <aside className="studio-sidebar">
        <button
          className="studio-mark"
          title="BlackRed Studio"
          onClick={() => setView("home")}
        >
          <span className="studio-mark-shape">
            <span />
            <span />
          </span>
        </button>

        <nav className="primary-nav" aria-label={t("topbar.local")}>
          {localizedNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                className={`nav-button ${view === item.id ? "is-active" : ""}`}
                key={item.id}
                onClick={() => setView(item.id)}
                title={`${item.label}${item.shortcut ? ` (Ctrl+${item.shortcut})` : ""}`}
              >
                <Icon size={19} strokeWidth={1.8} />
                <span>{item.label}</span>
              </button>
            );
          })}
          <div className="help-nav-wrap">
            <button
              className={`nav-button ${view.startsWith("help-") ? "is-active" : ""}`}
              onClick={() => setHelpMenuOpen((open) => !open)}
              title={t("nav.help")}
              aria-expanded={helpMenuOpen}
            >
              <HelpCircle size={19} strokeWidth={1.8} />
              <span>{t("nav.help")}</span>
            </button>
            {helpMenuOpen && (
              <div className="help-nav-menu">
                <button onClick={() => { setView("help-guide"); setHelpMenuOpen(false); }}><BookOpenCheck size={16}/><span><strong>{t("help.tabs.guide")}</strong><small>{t("help.tabs.guideHint")}</small></span></button>
                <button onClick={() => { setView("help-feedback"); setHelpMenuOpen(false); }}><MessageSquareText size={16}/><span><strong>{t("help.tabs.feedback")}</strong><small>{t("help.tabs.feedbackHint")}</small></span></button>
                <button onClick={() => { setView("help-knowledge"); setHelpMenuOpen(false); }}><Library size={16}/><span><strong>{t("help.tabs.knowledge")}</strong><small>{t("help.tabs.knowledgeHint")}</small></span></button>
              </div>
            )}
          </div>
        </nav>

        <div className="sidebar-bottom">
          <button
            className={`nav-button ${view === "settings" ? "is-active" : ""}`}
            onClick={() => setView("settings")}
            title={t("nav.settings")}
          >
            <Settings size={19} strokeWidth={1.8} />
            <span>{t("nav.settings")}</span>
          </button>
          <div className="sidebar-version">BRS / 0.2</div>
        </div>
      </aside>

      <div className="studio-main">
        <header className="topbar">
          <div className="topbar-left">
            <div className="project-switcher-wrap">
              <button
                className="project-switcher"
                onClick={() => setProjectMenuOpen((open) => !open)}
              >
                <span className="project-dot" />
                <span>
                  <small>{t("topbar.activeProject")}</small>
                  <strong>{project.name}</strong>
                </span>
                <ChevronDown size={15} />
              </button>
              {projectMenuOpen && (
                <div className="project-popover">
                  <div className="popover-label">{t("topbar.localSpace")}</div>
                  <button
                    className="project-popover-item is-current"
                    onClick={() => setProjectMenuOpen(false)}
                  >
                    <span className="project-avatar">BR</span>
                    <span>
                      <strong>{project.name}</strong>
                      <small>{project.slug}</small>
                    </span>
                    <Circle size={8} fill="currentColor" />
                  </button>
                  <button
                    className="popover-action"
                    onClick={() => {
                      setProjectMenuOpen(false);
                      setView("settings");
                    }}
                  >
                    <Settings size={15} />
                    {t("topbar.configureProject")}
                  </button>
                </div>
              )}
            </div>
            <span className="topbar-divider" />
            <div className="view-identity">
              <ActiveIcon size={16} />
              <span>{activeItem.label}</span>
            </div>
          </div>

          <div className="topbar-center">
            <button
              className="command-trigger"
              onClick={() => setCommandOpen(true)}
            >
              <Search size={15} />
              <span>{t("topbar.searchPlaceholder")}</span>
              <kbd>Ctrl K</kbd>
            </button>
          </div>

          <div className="topbar-actions">
            <select
              className="language-select"
              aria-label={t("language.label")}
              value={i18n.language}
              onChange={(event) => setLanguage(event.target.value as SupportedLanguage)}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <option key={lang} value={lang}>
                  {LANGUAGE_LABELS[lang]}
                </option>
              ))}
            </select>
            <span className="status-pill">
              <span className="status-dot" />
              {t("topbar.local")}
            </span>
            <button
              className="icon-button"
              title={t("topbar.darkTheme")}
              aria-label={t("topbar.darkTheme")}
            >
              <Moon size={17} />
            </button>
            <button
              className="run-button"
              onClick={() => setView("deploy")}
            >
              <Play size={15} fill="currentColor" />
              {t("topbar.run")}
            </button>
          </div>
        </header>

        <main className="workspace">{children}</main>
      </div>

      {commandOpen && (
        <div
          className="command-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setCommandOpen(false);
          }}
        >
          <section className="command-palette" aria-label={t("command.quickActions")}>
            <div className="command-search">
              <Command size={18} />
              <input
                autoFocus
                placeholder={t("command.whatToDo")}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setCommandOpen(false);
                }}
              />
              <kbd>ESC</kbd>
            </div>
            <div className="command-section-label">{t("command.quickActions")}</div>
            <div className="command-list">
              {quickCommands.map((command) => {
                const CommandIcon = command.icon;
                return (
                  <button
                    key={command.label}
                    onClick={() => {
                      setView(command.view);
                      setCommandOpen(false);
                    }}
                  >
                    <span className="command-icon">
                      <CommandIcon size={17} />
                    </span>
                    <span>
                      <strong>{command.label}</strong>
                      <small>{command.hint}</small>
                    </span>
                    <span className="command-enter">↵</span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      )}
      <BlackedOnboarding />
    </div>
  );
}
